import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Loader2, ArrowLeft, Briefcase, Shield, Search, Filter,
  CheckCircle, XCircle, Clock, Calendar, Mail, Phone,
  FileText, Download, Eye, ChevronDown, ChevronUp, User
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const AVIDA_LOGO = "https://customer-assets.emergentagent.com/job_packet-digitizer/artifacts/f0t7kezs_Avida%20Logo%20Full%20Color.png";

const STATUS_COLORS = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
  reviewed: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Eye },
  interview: { bg: 'bg-purple-100', text: 'text-purple-700', icon: Calendar },
  hired: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
};

export default function HRDashboardPage() {
  const [applications, setApplications] = useState([]);
  const [backgroundChecks, setBackgroundChecks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('applications');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const { user, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/clients');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [appsRes, checksRes] = await Promise.all([
        fetch(`${API_BASE}/api/employment/applications`, { headers }),
        fetch(`${API_BASE}/api/employment/background-checks`, { headers })
      ]);
      
      if (appsRes.ok) {
        const appsData = await appsRes.json();
        setApplications(appsData);
      }
      
      if (checksRes.ok) {
        const checksData = await checksRes.json();
        setBackgroundChecks(checksData);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load applications');
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (appId, newStatus) => {
    try {
      const res = await fetch(`${API_BASE}/api/employment/applications/${appId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(`Status updated to ${newStatus}`);
        loadData();
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const exportToCSV = () => {
    const data = activeTab === 'applications' ? applications : backgroundChecks;
    if (data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = activeTab === 'applications' 
      ? ['Name', 'Email', 'Phone', 'Position', 'Status', 'Submitted']
      : ['Name', 'SSN (Last 4)', 'DOB', 'Status', 'Submitted'];

    const rows = data.map(item => {
      if (activeTab === 'applications') {
        return [
          `${item.firstName} ${item.lastName}`,
          item.email,
          item.phone,
          item.positionDesired,
          item.status,
          new Date(item.created_at).toLocaleDateString()
        ];
      } else {
        return [
          `${item.firstName} ${item.lastName}`,
          item.ssn_last4 || 'N/A',
          item.dateOfBirth,
          item.status,
          new Date(item.created_at).toLocaleDateString()
        ];
      }
    });

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV exported');
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = searchQuery === '' || 
      `${app.firstName} ${app.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredChecks = backgroundChecks.filter(check => {
    const matchesSearch = searchQuery === '' || 
      `${check.firstName} ${check.lastName}`.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || check.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    interview: applications.filter(a => a.status === 'interview').length,
    hired: applications.filter(a => a.status === 'hired').length,
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A368F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="header-glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/admin')}
              className="text-slate-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Admin
            </Button>
            <img src={AVIDA_LOGO} alt="Avida" className="h-10" />
          </div>
          <span className="badge-navy">HR Dashboard</span>
        </div>
      </header>

      {/* Stats Cards */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 border-l-4 border-l-[#0A368F]">
            <div className="text-2xl font-bold text-[#0A368F]">{stats.total}</div>
            <div className="text-sm text-slate-500">Total Applications</div>
          </Card>
          <Card className="p-4 border-l-4 border-l-amber-500">
            <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
            <div className="text-sm text-slate-500">Pending Review</div>
          </Card>
          <Card className="p-4 border-l-4 border-l-purple-500">
            <div className="text-2xl font-bold text-purple-600">{stats.interview}</div>
            <div className="text-sm text-slate-500">In Interview</div>
          </Card>
          <Card className="p-4 border-l-4 border-l-green-500">
            <div className="text-2xl font-bold text-green-600">{stats.hired}</div>
            <div className="text-sm text-slate-500">Hired</div>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-2">
            <Button
              variant={activeTab === 'applications' ? 'default' : 'outline'}
              onClick={() => setActiveTab('applications')}
              className={activeTab === 'applications' ? 'btn-navy' : ''}
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Applications ({applications.length})
            </Button>
            <Button
              variant={activeTab === 'background' ? 'default' : 'outline'}
              onClick={() => setActiveTab('background')}
              className={activeTab === 'background' ? 'btn-navy' : ''}
            >
              <Shield className="w-4 h-4 mr-2" />
              Background Checks ({backgroundChecks.length})
            </Button>
          </div>
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 px-3 rounded-lg border border-slate-200 text-sm"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="reviewed">Reviewed</option>
              <option value="interview">Interview</option>
              <option value="hired">Hired</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Applications List */}
        {activeTab === 'applications' && (
          <div className="space-y-4">
            {filteredApplications.length === 0 ? (
              <Card className="p-12 text-center">
                <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No applications found</p>
              </Card>
            ) : (
              filteredApplications.map((app) => {
                const statusConfig = STATUS_COLORS[app.status] || STATUS_COLORS.pending;
                const StatusIcon = statusConfig.icon;
                const isExpanded = expandedId === app.id;
                
                return (
                  <Card key={app.id} className="overflow-hidden">
                    <div 
                      className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => setExpandedId(isExpanded ? null : app.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-[#0A368F]/10 flex items-center justify-center">
                            <User className="w-6 h-6 text-[#0A368F]" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-800">
                              {app.firstName} {app.lastName}
                            </h3>
                            <p className="text-sm text-slate-500">{app.positionDesired}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig.bg} ${statusConfig.text}`}>
                            <StatusIcon className="w-3 h-3" />
                            {app.status}
                          </span>
                          <span className="text-sm text-slate-400">
                            {new Date(app.created_at).toLocaleDateString()}
                          </span>
                          {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                        </div>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="border-t bg-slate-50 p-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Contact Info */}
                          <div>
                            <h4 className="font-semibold text-slate-700 mb-3">Contact Information</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-slate-400" />
                                <a href={`mailto:${app.email}`} className="text-[#0A368F] hover:underline">{app.email}</a>
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-slate-400" />
                                <span>{app.phone}</span>
                              </div>
                              <div className="text-slate-600">
                                {app.address}, {app.city}, {app.state} {app.zipCode}
                              </div>
                            </div>
                          </div>
                          
                          {/* Employment Info */}
                          <div>
                            <h4 className="font-semibold text-slate-700 mb-3">Employment Details</h4>
                            <div className="space-y-2 text-sm">
                              <div><span className="text-slate-500">Position:</span> {app.positionDesired}</div>
                              <div><span className="text-slate-500">Type:</span> {app.employmentType}</div>
                              <div><span className="text-slate-500">Available:</span> {app.startDate}</div>
                              <div><span className="text-slate-500">SSN:</span> {app.ssn_masked || 'XXX-XX-' + (app.ssn_last4 || '****')}</div>
                            </div>
                          </div>
                          
                          {/* Work History */}
                          {app.employer1Name && (
                            <div className="md:col-span-2">
                              <h4 className="font-semibold text-slate-700 mb-3">Work History</h4>
                              <div className="bg-white p-3 rounded-lg border text-sm">
                                <div className="font-medium">{app.employer1Name}</div>
                                <div className="text-slate-500">{app.employer1Position}</div>
                                <div className="text-slate-400 text-xs">{app.employer1StartDate} - {app.employer1EndDate || 'Present'}</div>
                              </div>
                            </div>
                          )}
                          
                          {/* References */}
                          {app.ref1Name && (
                            <div className="md:col-span-2">
                              <h4 className="font-semibold text-slate-700 mb-3">References</h4>
                              <div className="grid sm:grid-cols-3 gap-3">
                                {app.ref1Name && (
                                  <div className="bg-white p-3 rounded-lg border text-sm">
                                    <div className="font-medium">{app.ref1Name}</div>
                                    <div className="text-slate-500">{app.ref1Relationship}</div>
                                    <div className="text-slate-400">{app.ref1Phone}</div>
                                  </div>
                                )}
                                {app.ref2Name && (
                                  <div className="bg-white p-3 rounded-lg border text-sm">
                                    <div className="font-medium">{app.ref2Name}</div>
                                    <div className="text-slate-500">{app.ref2Relationship}</div>
                                    <div className="text-slate-400">{app.ref2Phone}</div>
                                  </div>
                                )}
                                {app.ref3Name && (
                                  <div className="bg-white p-3 rounded-lg border text-sm">
                                    <div className="font-medium">{app.ref3Name}</div>
                                    <div className="text-slate-500">{app.ref3Relationship}</div>
                                    <div className="text-slate-400">{app.ref3Phone}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t">
                          <span className="text-sm text-slate-500 mr-2">Update Status:</span>
                          {['pending', 'reviewed', 'interview', 'hired', 'rejected'].map(status => (
                            <Button
                              key={status}
                              size="sm"
                              variant={app.status === status ? 'default' : 'outline'}
                              onClick={(e) => {
                                e.stopPropagation();
                                updateStatus(app.id, status);
                              }}
                              className={app.status === status ? 'btn-navy' : ''}
                            >
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Background Checks List */}
        {activeTab === 'background' && (
          <div className="space-y-4">
            {filteredChecks.length === 0 ? (
              <Card className="p-12 text-center">
                <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No background checks found</p>
              </Card>
            ) : (
              filteredChecks.map((check) => {
                const statusConfig = STATUS_COLORS[check.status] || STATUS_COLORS.pending;
                const StatusIcon = statusConfig.icon;
                const isExpanded = expandedId === check.id;
                
                // Count how many questions answered "Yes"
                const yesCount = [1,2,3,4,5,6,7,8,9,10,11,12].filter(n => check[`q${n}`] === true).length;
                
                return (
                  <Card key={check.id} className="overflow-hidden">
                    <div 
                      className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => setExpandedId(isExpanded ? null : check.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                            <Shield className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-800">
                              {check.firstName} {check.lastName}
                            </h3>
                            <p className="text-sm text-slate-500">
                              SSN: XXX-XX-{check.ssn_last4 || '****'} | DOB: {check.dateOfBirth}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {yesCount > 0 && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              {yesCount} disclosure(s)
                            </span>
                          )}
                          <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig.bg} ${statusConfig.text}`}>
                            <StatusIcon className="w-3 h-3" />
                            {check.status}
                          </span>
                          <span className="text-sm text-slate-400">
                            {new Date(check.created_at).toLocaleDateString()}
                          </span>
                          {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                        </div>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="border-t bg-slate-50 p-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Personal Info */}
                          <div>
                            <h4 className="font-semibold text-slate-700 mb-3">Personal Information</h4>
                            <div className="space-y-2 text-sm">
                              <div><span className="text-slate-500">Name:</span> {check.firstName} {check.middleName} {check.lastName}</div>
                              <div><span className="text-slate-500">DOB:</span> {check.dateOfBirth}</div>
                              <div><span className="text-slate-500">Sex:</span> {check.sex}</div>
                              <div><span className="text-slate-500">Phone:</span> {check.phone}</div>
                              <div><span className="text-slate-500">Address:</span> {check.address}, {check.city}, {check.state} {check.zipCode}</div>
                            </div>
                          </div>
                          
                          {/* Disclosure Summary */}
                          <div>
                            <h4 className="font-semibold text-slate-700 mb-3">Disclosure Summary</h4>
                            <div className="space-y-1 text-sm">
                              {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                                <div key={n} className="flex items-center gap-2">
                                  {check[`q${n}`] ? (
                                    <XCircle className="w-4 h-4 text-red-500" />
                                  ) : (
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                  )}
                                  <span className={check[`q${n}`] ? 'text-red-700' : 'text-slate-600'}>
                                    Q{n}: {check[`q${n}`] ? 'Yes' : 'No'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Disclosed Details */}
                          {yesCount > 0 && (
                            <div className="md:col-span-2">
                              <h4 className="font-semibold text-red-700 mb-3">Disclosed Information</h4>
                              <div className="space-y-2">
                                {[1,2,3,4,5,6,7,8,9,10,11,12].filter(n => check[`q${n}`] && check[`q${n}Detail`]).map(n => (
                                  <div key={n} className="bg-red-50 p-3 rounded-lg border border-red-200 text-sm">
                                    <div className="font-medium text-red-800">Question {n}</div>
                                    <div className="text-red-700">{check[`q${n}Detail`]}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })
            )}
          </div>
        )}
      </section>
    </div>
  );
}
